import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'

interface Toast {
  id: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (message: string, type: Toast['type']) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let toastIdCounter = 0

// Global toast object for imperative API
export const toast = {
  info: (message: string) => globalAddToast?.(message, 'info'),
  success: (message: string) => globalAddToast?.(message, 'success'),
  warning: (message: string) => globalAddToast?.(message, 'warning'),
  error: (message: string) => globalAddToast?.(message, 'error'),
}

let globalAddToast: ((message: string, type: Toast['type']) => void) | null = null

function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((message: string, type: Toast['type']) => {
    const id = `toast-${++toastIdCounter}`
    setToasts((prev) => [...prev, { id, message, type }])

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 3500)
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  globalAddToast = addToast

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  )
}

function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

const TOAST_STYLE: Record<Toast['type'], { icon: string; color: string }> = {
  success: { icon: 'lucide:circle-check', color: 'text-emerald-400' },
  info:    { icon: 'lucide:info',         color: 'text-sky-400' },
  warning: { icon: 'lucide:triangle-alert', color: 'text-amber-400' },
  error:   { icon: 'lucide:circle-x',     color: 'text-red-400' },
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const { icon, color } = TOAST_STYLE[toast.type]

  return (
    <div
      role="status"
      className="inline-flex items-center gap-2.5 pl-3.5 pr-2 py-2.5 rounded-full border animate-in fade-in slide-in-from-top-2 duration-200"
      style={{
        backgroundColor: '#1C1F2E',
        borderColor: '#2A2D3A',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
      }}
    >
      <Icon icon={icon} className={`w-4 h-4 flex-none ${color}`} />
      <span className="text-sm text-white font-medium leading-none">{toast.message}</span>
      <button
        onClick={onClose}
        className="flex-none w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-300 hover:bg-white/5"
        aria-label="关闭"
      >
        <Icon icon="lucide:x" className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function ToastContainer() {
  const { toasts, removeToast } = useToast()

  if (toasts.length === 0) return null

  return createPortal(
    <div className="fixed top-6 left-0 right-0 z-[100] flex flex-col items-center gap-2 px-4 pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} onClose={() => removeToast(t.id)} />
        </div>
      ))}
    </div>,
    document.body
  )
}

export function Toaster() {
  return (
    <ToastProvider>
      <ToastContainer />
    </ToastProvider>
  )
}
